import { isEmpty } from "lodash";

/**
 * Takes successful axios response and formats it to allow throw
 * @param error
 * @returns {{response: {data: {error: *}}}}
 */
export function formatError(error) {
  return {
    response: {
      data: { error },
    },
  };
}

/**
 * Perform a "deep" copy of an object, preventing nested object as reference
 * @param {object}  object
 * @return {object}
 */
export function cloneObject(object) {
  return JSON.parse(JSON.stringify(object));
}

/**
 * Convert the permission_ch to human readable values
 * @param   {string}  permission    U | G | C
 * @returns {string}
 */
export function humanizePermission(permission) {
  switch (permission) {
    case "U":
      return "Private";
    case "G":
      return "Group";
    case "C":
      return "Company";
    default:
      return "Unknown";
  }
}

/**
 * Convert the group_id_bint to human readable values
 * @param   {string}  permission    1 | 2 | 3 | 4
 * @returns {string}
 */
export function humanizeGroup(permission) {
  switch (permission) {
    case 1:
      return "Read Only";
    case 2:
      return "User";
    case 3:
      return "Admin";
    case 4:
      return "Super Admin";
    case 5:
      return "Internal Admin";
    default:
      return "Unknown";
  }
}

/**
 * Ensures phone number parsing consistency by removing leading 1's and non-numerical characters
 * @param   {string} str  The input phone number string
 * @returns {string}
 */
export function stripPhoneNumber(str) {
  // Remove everything that isn't a number: +1 (222) 333-4444 => 12223334444
  str = str.replace(/[^\d]/g, "");

  // Prevent mis-parsing by removing leading 1
  if (/^1/.test(str)) {
    str = str.slice(1);
  }
  return str;
}

/**
 * Converts a phone number string into a human readable standard
 * @param   {string} str  The input phone number string
 * @returns {string}
 */
export function readablePhoneNumber(str) {
  if (!str) {
    return "";
  }
  str = stripPhoneNumber(str);

  return `1 (${str.substr(0, 3)}) ${str.substr(3, 3)}-${str.substr(6, 4)}`;
}

export function datasetStatus(status) {
  switch (status) {
    case -1:
      return "error";
    case 0:
      return "editing";
    case 1:
      return "processing";
    case 2:
      return "created";
    default:
      return "Unknown";
  }
}

export function campaignUIToServer(campaign) {
  // Normalize data to remove react-select formatting
  campaign.folder = { folder_id_bint: campaign.folder.value, title_vch: campaign.folder.label };
  if (campaign.dataset && campaign.dataset.value) {
    campaign.dataset = campaign.dataset.value;
  }
  ["type", "subType"].forEach((type) => {
    campaign[type] = normalizeSelectOption(campaign[type]);
  });

  ["email", "sms", "voice", "mapped_variables"].forEach((type) => {
    campaign[type] = normalizeTemplate(campaign[type]);
  });

  return campaign;
}

function normalizeSelectOption(option) {
  let normalized_value = null;

  if (option && option.value) {
    normalized_value = option.value;
  }

  return normalized_value;
}

/**
 * Normalize the various formats to try to get a consistent key:value while actual value may need to store metadata
 * @param {Object} template
 * @returns {Object}
 */
export function normalizeTemplate(template) {
  const normalized_template = {};

  if (isEmpty(template) || template.enabled === false) {
    return null;
  }

  Object.entries(template).forEach(([key, value]) => {
    if (value === null) {
      // Unselected value
      return;
    }

    // Mapped variables
    if (value.source) {
      delete value.selection;
      normalized_template[key] = value;
      return;
    }

    // Template options
    switch (key) {
      case "enabled":
        // ignore
        break;
      case "short_code": // SMS
      case "phone_number": // VOICE
      case "retries":
      case "repeat_key":
      case "dtmf1_key":
      case "dtmf2_key":        
      case "live_agent_transfer_key":
        if (value.value !== undefined) {
          normalized_template[key] = value.value;
          break;
        }
      // eslint-disable-next-line no-fallthrough
      default:
        normalized_template[key] = value;
    }
  });

  return normalized_template;
}
